import React, { useContext, useEffect, useState } from 'react';

import {
  type GuidedOnboardingPayload,
  type IGroupDefinition,
} from 'loot-core/shared/onboarding';

import { OnboardingContext } from '../../../../context/onboarding/OnboardingContext';
import { AsyncStorageKey, useStorage } from '../../../../hooks/useStorage';
import { SvgCheckmark, SvgClose } from '../../../../icons/v1';
import { theme } from '../../../../style';
import { Button } from '../../../common/Button';
import { Card } from '../../../common/Card';
import { Input } from '../../../common/Input';
import { Text } from '../../../common/Text';
import { View } from '../../../common/View';
import { FormField, FormLabel } from '../../../forms';

type OnboardingCheckboxProps = {
  groupDefinition: IGroupDefinition;
};

export function OnboardingCheckbox({
  groupDefinition,
}: OnboardingCheckboxProps) {
  const { collectData } = useContext(OnboardingContext);
  const { getItem: getOnboardingData } = useStorage(AsyncStorageKey.ONBOARDING_DATA);
  const [categories, setCategories] = useState<{ name: string }[]>(
    groupDefinition.categories || [],
  );
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [showNewCategoryForm, setShowNewCategoryForm] =
    useState<boolean>(false);
  const [newCategoryName, setNewCategoryName] = useState<string>('');

  useEffect(() => {
    (async () => {
      const onboardingData: GuidedOnboardingPayload = await getOnboardingData();

      if (onboardingData) {
        const group = onboardingData.groups?.find(
          g => g.name === groupDefinition.name,
        );

        if (group && group.categories?.length) {
          setSelectedCategories(group.categories);
        }

        if (group) {
          const newCategories = [];
          for (const catName of group.categories) {
            const index = groupDefinition.categories.findIndex(
              category => category.name === catName,
            );

            if (index === -1) {
              newCategories.push({ name: catName });
            }
          }

          if (newCategories.length) {
            setCategories([...categories, ...newCategories]);
          }
        }
      }
    })();
  }, []);

  return (
    <View style={{ marginBottom: 20, gap: 10 }}>
      <Text style={{ fontWeight: 500 }}>{groupDefinition.name}</Text>
      {categories.map((category, catIndex) => {
        return (
          <Card
            key={category.name}
            style={{
              padding: 10,
              marginTop: 0,
              cursor: 'pointer',
              ...(selectedCategories.includes(category.name)
                ? {
                    color: 'white',
                    backgroundColor: theme.buttonPrimaryBackground,
                  }
                : {}),
            }}
            onClick={() => updateSelectedCategories(category.name)}
          >
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <Text>{category.name}</Text>
              {selectedCategories.includes(category.name) ? (
                <SvgCheckmark width={14} height={14} />
              ) : null}
            </View>
          </Card>
        );
      })}
      {showNewCategoryForm ? (
        <Card style={{ padding: 10, minHeight: 'auto' }}>
          <Button
            style={{
              alignSelf: 'flex-end',
              position: 'absolute',
              zIndex: 10,
              top: 0,
            }}
            type="bare"
            isSubmit={false}
            onClick={e => {
              onNewCategoryHide();
            }}
          >
            <SvgClose width={10} />
          </Button>
          <View style={{ flexDirection: 'row', gap: 3 }}>
            <FormField style={{ flex: 1 }}>
              <FormLabel
                title="Category name"
                htmlFor="categoryName"
                style={{ lineHeight: '22px' }}
              />
              <Input
                id="categoryName"
                value={newCategoryName}
                required={true}
                onChange={ev => setNewCategoryName(ev.target.value)}
              />
            </FormField>
            <Button
              type="primary"
              isSubmit={true}
              style={{ alignSelf: 'flex-end' }}
              onClick={() => {
                if (newCategoryName) {
                  setCategories([...categories, { name: newCategoryName }]);
                  updateSelectedCategories(newCategoryName);
                }
                onNewCategoryHide();
              }}
            >
              Add
            </Button>
          </View>
        </Card>
      ) : (
        <Button
          style={{ alignSelf: 'flex-end' }}
          type="normal"
          isSubmit={false}
          onClick={e => {
            setShowNewCategoryForm(true);
          }}
        >
          Add another category
        </Button>
      )}
    </View>
  );

  function onNewCategoryHide() {
    setNewCategoryName('');
    setShowNewCategoryForm(false);
  }

  async function updateSelectedCategories(categoryName: string) {
    const _selectedCategories = [...selectedCategories];
    const index = _selectedCategories.indexOf(categoryName);

    if (index === -1) {
      _selectedCategories.push(categoryName);
    } else {
      _selectedCategories.splice(index, 1);
    }

    setSelectedCategories(_selectedCategories);
    await collectData?.(groupDefinition.name, _selectedCategories);
  }
}
